import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from './navbar';
import Footer from './Footer';
import { FiSearch, FiInfo, FiMapPin, FiDollarSign, FiLayers } from 'react-icons/fi';
import { BASE_URL } from '../apiConfig';

interface VC {
  name: string;
  description: string;
  website?: string;
  headquarters?: string;
  founded_year?: string;
  aum?: string;
  investment_focus?: string[];
  stage_focus?: string;
  geographic_focus?: string[];
  portfolio_count?: number;
  notable_portfolio?: string[];
  key_partners?: { name: string; focus_areas?: string }[];
  source?: string[];
}

interface VCApiResponse {
  status: string;
  data: VC[];
  query_used?: string;
  message?: string;
}

const ITEMS_PER_PAGE = 6;

const VentureCapitalistPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [previousQuery, setPreviousQuery] = useState('');
  const [results, setResults] = useState<VC[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [queryUsed, setQueryUsed] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Autocomplete
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestWrapRef = useRef<HTMLDivElement | null>(null);

  // Restore session
  useEffect(() => {
    const saved = sessionStorage.getItem('VentureCapitalistData');
    if (saved) {
      try {
        const p = JSON.parse(saved);
        setResults(p.results || []);
        setQueryUsed(p.queryUsed || null);
        setPreviousQuery(p.previousQuery || '');
        setHasSearched(p.hasSearched || false);
        setCurrentPage(p.currentPage || 1);
      } catch { }
    }
  }, []);

  useEffect(() => {
    if (hasSearched) {
      sessionStorage.setItem('VentureCapitalistData', JSON.stringify({
        results, queryUsed, previousQuery, hasSearched, currentPage,
      }));
    }
  }, [results, queryUsed, previousQuery, hasSearched, currentPage]);

  // Debounced autocomplete
  useEffect(() => {
    const q = searchQuery.trim();
    if (loading || q.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setSuggestLoading(true);
        const res = await fetch(`${BASE_URL}/api/autocomplete?q=${encodeURIComponent(q)}`, { signal: controller.signal });
        const data = await res.json();
        const list: string[] = Array.isArray(data?.suggestions) ? data.suggestions.slice(0, 8) : [];
        setSuggestions(list);
        setShowSuggestions(list.length > 0);
        setActiveSuggestion(-1);
      } catch { } finally { setSuggestLoading(false); }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [searchQuery, loading]);

  const handleSearch = async (inboundQuery?: string) => {
    const q = (inboundQuery ?? searchQuery).trim();
    if (!q) return;
    setLoading(true);
    setHasSearched(true);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveSuggestion(-1);
    setCurrentPage(1);
    setPreviousQuery(q);
    setResults([]);

    try {
      const response = await fetch(`${BASE_URL}/api/v1/vc/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ search_query: q }),
      });
      const data: VCApiResponse = await response.json();
      if (data.status === 'success') {
        setResults(data.data || []);
        setQueryUsed(data.query_used || null);
      } else {
        setResults([]);
      }
    } catch (e) {
      console.error('VC search error:', e);
      setResults([]);
    }
    setLoading(false);
    setSearchQuery('');
  };

  const handleSuggestionSelect = (s: string) => {
    setSearchQuery(s);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveSuggestion(-1);
    setTimeout(() => handleSearch(s), 0);
  };

  const visibleResults = results.slice(0, currentPage * ITEMS_PER_PAGE);
  const hasMore = currentPage * ITEMS_PER_PAGE < results.length;

  return (
    <div className="min-h-screen relative flex flex-col overflow-x-hidden"
      style={{ background: "radial-gradient(35% 19% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black", color: "white" }}>
      <Navbar />

      <style>{`
        @media (max-width: 640px) { .bg-dots { display: none !important; } }
        input::placeholder { color: rgba(255,255,255,0.28) !important; }
      `}</style>

      <div className="absolute bg-dots" style={{ width: "1106px", height: "500px", top: "-12px", left: "194px", backgroundImage: "url('/dots.png')", backgroundSize: "contain", backgroundRepeat: "no-repeat", pointerEvents: "none", userSelect: "none", zIndex: 0 }} />

      {/* Hero */}
      <div className="w-full flex flex-col items-center justify-center px-4 text-center pt-28 pb-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontFamily: 'IBM Plex Sans', fontWeight: 700, fontSize: '48px', lineHeight: '110%', letterSpacing: '-1px' }}>
            VC Directory
          </h1>
          <p style={{ fontFamily: 'Lora', fontWeight: 400, fontSize: '18px', color: '#9CA3AF', maxWidth: '720px', margin: '10px auto 0', padding: '16px 24px' }}>
            Find venture capital firms by location, sector, and stage — powered by AI search.
          </p>
        </motion.div>

        {/* Search bar */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="w-full max-w-2xl mx-auto mt-8">
          <div className="relative w-full" ref={suggestWrapRef}>
            <div className="flex items-center w-full overflow-hidden" style={{ height: 48 }}>
              <div className="flex-1 flex items-center rounded-l-full border border-[#F9F9F9] bg-black/80 pr-2" style={{ height: '100%' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => !loading && setShowSuggestions(suggestions.length > 0)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  onKeyDown={e => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setShowSuggestions(true); setActiveSuggestion(i => Math.min(i + 1, suggestions.length - 1)); return; }
                    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveSuggestion(i => Math.max(i - 1, -1)); return; }
                    if (e.key === 'Escape') { setShowSuggestions(false); return; }
                    if (e.key === 'Enter') {
                      if (activeSuggestion >= 0 && suggestions[activeSuggestion]) { e.preventDefault(); handleSuggestionSelect(suggestions[activeSuggestion]); return; }
                      handleSearch();
                    }
                  }}
                  placeholder="e.g. US-based startup investors, Seed stage AI VCs in India"
                  className="flex-1 px-6 py-0 outline-none border-none bg-transparent"
                  style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 18, color: '#F9F9F9', height: '100%' }}
                />
                {suggestLoading && <div className="mr-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/70" /></div>}
              </div>
              <button className="h-full bg-[#FF541F] hover:bg-orange-600 text-white font-semibold px-8 rounded-r-full transition flex items-center gap-2"
                style={{ fontSize: 18 }} onClick={() => handleSearch()} disabled={loading}>
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : <><FiSearch />Search</>}
              </button>
            </div>

            {showSuggestions && !loading && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 mt-2 bg-[#0F0F10] border border-white/10 rounded-xl shadow-xl z-20 overflow-hidden">
                {suggestions.map((s, idx) => (
                  <button key={idx} type="button" onMouseDown={e => e.preventDefault()} onClick={() => handleSuggestionSelect(s)}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${idx === activeSuggestion ? 'bg-white/10 text-white' : 'hover:bg-white/5 text-gray-200'}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {hasSearched && results.length === 0 && !loading && (
            <div className="mt-6 bg-zinc-900/80 border border-white/10 rounded-xl px-6 py-8 text-center">
              <h2 className="text-lg font-semibold text-white mb-2">No VCs found</h2>
              <p className="text-gray-400 text-sm">Try adjusting your query — e.g. "Sequoia-stage VC India" or "US seed investors fintech"</p>
            </div>
          )}

          {previousQuery && (
            <div className="mt-4 text-left">
              <div className="inline-flex items-start gap-2 text-xs text-gray-300 bg-white/5 border border-white/10 rounded-md px-3 py-2">
                <FiInfo className="mt-0.5 text-blue-300 shrink-0" />
                <div><span className="text-gray-400">Query:</span> <span className="text-gray-200">{previousQuery}</span></div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {results.length === 0 && !loading && <div className="flex-grow min-h-[50vh]" />}

      {/* Results */}
      {results.length > 0 && (
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleResults.map((vc, idx) => (
              <VCCard key={idx} vc={vc} onDetails={() => navigate(`/solution-company/${encodeURIComponent(vc.name)}`, {
                state: { companyName: vc.name, title: previousQuery, description: vc.description }
              })} />
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center gap-4">
            <p className="text-sm text-neutral-400">
              Showing <span className="text-white font-semibold">{visibleResults.length}</span> of <span className="text-white font-semibold">{results.length}</span> results
            </p>
            {hasMore && (
              <button onClick={() => setCurrentPage(p => p + 1)}
                className="px-8 py-3.5 rounded-full font-semibold text-sm bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg transition-all flex items-center gap-3">
                Load More Results
              </button>
            )}
            {!hasMore && results.length > 0 && (
              <p className="text-sm text-neutral-400">You've reached the end of the results</p>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default VentureCapitalistPage;

/* ── VC Card ── */
const VCCard = ({ vc, onDetails }: { vc: VC; onDetails: () => void }) => {
  const [openSource, setOpenSource] = useState(false);

  return (
    <div className="relative flex flex-col w-full rounded-2xl p-7 overflow-visible"
      style={{
        background: "linear-gradient(0deg, rgba(39,40,41,0.7), rgba(39,40,41,0.7)), linear-gradient(119.3deg, rgba(0,0,0,0) 23.34%, rgba(255,60,0,0.76) 99.36%)",
        zIndex: openSource ? 1000 : 1, minHeight: 200,
      }}>

      {/* Name + HQ */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-[Sora] font-semibold text-[1rem] md:text-[1.1rem] leading-tight text-white flex-1">{vc.name}</h3>
        {vc.headquarters && (
          <span className="flex items-center gap-1 text-[0.65rem] px-2.5 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 whitespace-nowrap shrink-0">
            <FiMapPin className="w-3 h-3" />{vc.headquarters}
          </span>
        )}
      </div>

      {/* Description */}
      <p className="font-[Sora] text-[0.85rem] leading-relaxed text-neutral-300/90 flex-1 mb-3 line-clamp-4">{vc.description}</p>

      {/* Stage focus */}
      {vc.stage_focus && (
        <div className="flex items-center gap-2 text-[0.75rem] text-neutral-300 mb-2">
          <FiLayers className="w-3.5 h-3.5 text-orange-400 shrink-0" />
          <span className="text-neutral-400">Stage:</span> {vc.stage_focus}
        </div>
      )}

      {/* AUM */}
      {vc.aum && (
        <div className="flex items-center gap-2 text-[0.75rem] text-neutral-300 mb-2">
          <FiDollarSign className="w-3.5 h-3.5 text-green-400 shrink-0" />
          <span className="text-neutral-400">AUM:</span> {vc.aum}
        </div>
      )}

      {/* Investment focus tags */}
      {vc.investment_focus && vc.investment_focus.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {vc.investment_focus.slice(0, 4).map((f, i) => (
            <span key={i} className="text-[0.65rem] px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-300">{f}</span>
          ))}
        </div>
      )}

      {/* Notable portfolio */}
      {vc.notable_portfolio && vc.notable_portfolio.length > 0 && (
        <div className="text-[0.72rem] text-neutral-400 mb-3">
          <span className="text-neutral-500">Portfolio: </span>
          {vc.notable_portfolio.slice(0, 3).join(', ')}
          {vc.notable_portfolio.length > 3 && ` +${vc.notable_portfolio.length - 3} more`}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center mt-auto pt-2 gap-3 relative">
        {/* Source */}
        {vc.source && vc.source.length > 0 && (
          <div className="relative">
            <button onClick={() => setOpenSource(!openSource)}
              className="flex items-center gap-2 text-[0.7rem] rounded-full px-4 py-2 bg-neutral-700/30 border border-neutral-600/40 text-neutral-200 hover:text-white transition">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Source
              <svg className={`w-3 h-3 transition-transform ${openSource ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openSource && (
              <div className="absolute left-0 mt-2 w-[min(440px,90vw)] z-[2000] rounded-xl p-2.5"
                style={{ background: "rgba(18,18,20,0.95)", border: "1px solid rgba(67,72,84,0.6)", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
                <ul className="flex flex-col gap-2 list-none m-0 p-0">
                  {vc.source!.map((src, i) => {
                    if (!src) return null;
                    let domain = src, path = "";
                    try { const u = new URL(src); domain = u.hostname.replace(/^www\./i, ""); path = u.pathname || "/"; } catch { }
                    return (
                      <li key={i} className="flex items-center gap-3 rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.01)", border: "1px solid rgba(54,63,84,0.35)" }}>
                        <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} alt="" style={{ width: 24, height: 24, borderRadius: 4, flexShrink: 0 }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <a href={src} target="_blank" rel="noopener noreferrer" className="block text-blue-300 font-semibold text-sm truncate">{domain}</a>
                          <div className="text-xs text-neutral-400 truncate mt-0.5">{path}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        <button onClick={onDetails}
          className="flex items-center gap-2 text-[0.7rem] rounded-full px-4 py-2 bg-neutral-700/30 border border-neutral-600/40 text-neutral-200 hover:text-white transition ml-auto">
          <FiInfo className="w-3.5 h-3.5" /> Details
        </button>
      </div>
    </div>
  );
};
